import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Download, FileSpreadsheet, FileText, Loader2, Store as StoreIcon } from 'lucide-react';
import { SettingsSheet, SheetBlock, SheetSection } from '../settings/sheet';
import { LoadFailed } from '../settings/instantSettings';
import { KeyValue, Notice } from '../shared/listPage';
import { HomeHeader } from '../home/HomeHeader';
import { KpisSkeleton, SectionsSkeleton } from '../home/HomeSkeleton';
import { KpiDelta, KpiItem, KpiStrip } from '../home/KpiStrip';
import { Bars, BarDatum, StackedBar, Tone, useVizTones } from '../home/charts';
import { Fig, Note, rich } from '../home/ui';
import {
  addDaysYmd, athensYmd, daysBetween, fmtDM, fmtDMY, fmtEur0, fmtMoney, fmtNum, fmtPct, fmtWeekday, fmtWeekdayShort, isYmd, toAthensYmd,
} from '../home/format';
import '../home/home.css';
import { formatMoney } from '../../utils/formatMoney';
import { OrgAnalyticsState, OrgRangeKey } from './useOrgAnalytics';
import { OrgAnalytics, OrgStore } from './orgTypes';
import { orderTypeLabel, paymentLabel, pctChange, storeLabel } from './orgFormat';
import './org.css';

/**
 * «Αρχική» των κεντρικών (νέα εμφάνιση, 14/10/2026): ίδιο «Ήσυχο ταμπλό» με την Αρχική του καταστήματος —
 * φύλλο με ευρετήριο, κεφαλίδα με περίοδο/καταστήματα/εξαγωγή, πέντε νούμερα σε λωρίδα (όχι κάρτες) και
 * ενότητες «συμπέρασμα | γράφημα ή λίστα». Ό,τι έδειχναν οι παλιές κάρτες με τα χρωματιστά εικονίδια είναι
 * εδώ: ανά ημέρα, ώρες αιχμής, ημέρα εβδομάδας, κανάλια, πληρωμές, προϊόντα, κατηγορίες, οικονομικά,
 * σύγκριση καταστημάτων (με «Είσοδο»), φύρα και υγεία καταλόγου. Ίδια δεδομένα, ίδιο endpoint.
 */

type TFn = (key: string, opts?: Record<string, unknown>) => string;

export interface OrgOverviewTabProps {
  analytics: OrgAnalyticsState;
  stores: OrgStore[];
  switching: number | null;
  onSwitchStore: (storeId: number) => void;
  /** Παραμετροποιήσιμη αναφορά (ExportReportModal). */
  onOpenReport: (format: 'pdf' | 'excel') => void;
  /** «Ανάλυση προϊόντων» — Excel συναλλαγής × προϊόν. */
  onExportExcel: () => void;
  exporting: boolean;
}

const RANGE_KEYS: OrgRangeKey[] = ['today', '7', '30', '90', 'custom'];
// Η εβδομάδα από τη Δευτέρα (ο server δίνει 0 = Κυριακή).
const WEEK = [1, 2, 3, 4, 5, 6, 0];

const arrow = (v: number) => (v > 0 ? '↑' : v < 0 ? '↓' : '');
/** Δ της λωρίδας: σύντομο «έναντι προηγ.» (χωρά στο κελί)· ολόκληρο το διάστημα σύγκρισης στο title. */
const deltaOf = (cur: number, prev: number, suffix: string, title?: string): KpiDelta => {
  const v = pctChange(cur, prev);
  return {
    text: v === 0 ? '—' : `${arrow(v)} ${fmtNum(Math.abs(v))}%`,
    tone: v === 0 ? 'flat' : v > 0 ? 'ok' : 'bad',
    suffix,
    title,
  };
};

const toneOf = (i: number): Tone => (i === 0 ? 1 : i === 1 ? 2 : i === 2 ? 3 : 'o');

/* ───────── περίοδος ───────── */

const OrgPeriodPicker: React.FC<{
  range: OrgRangeKey;
  customFrom: string;
  customTo: string;
  onRange: (r: OrgRangeKey) => void;
  onCustom: (from: string, to: string) => void;
}> = ({ range, customFrom, customTo, onRange, onCustom }) => {
  const { t } = useTranslation(['orgDashboard', 'listPage']);
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(customFrom);
  const [to, setTo] = useState(customTo);
  const boxRef = useRef<HTMLDivElement>(null);
  const customRef = useRef<HTMLButtonElement>(null);
  // Σήμερα ώρα Ελλάδας: το όριο «Έως» (ως 24/10/2026 UTC — από 00:00 ως 03:00 δεν επέτρεπε το σήμερα).
  const today = athensYmd();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        customRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const label = (key: OrgRangeKey): string => {
    if (key !== 'custom') return t(`orgDashboard:overview.range.${key}`);
    if (range === 'custom' && isYmd(customFrom) && isYmd(customTo)) {
      return customFrom === customTo ? fmtDM(customFrom) : `${fmtDM(customFrom)} – ${fmtDM(customTo)}`;
    }
    return t('orgDashboard:overview.range.custom');
  };

  const pick = (key: OrgRangeKey) => {
    if (key === 'custom') {
      if (!open) {
        setFrom(customFrom);
        setTo(customTo);
      }
      setOpen(o => !o);
      return;
    }
    setOpen(false);
    onRange(key);
  };

  const valid = isYmd(from) && isYmd(to) && from <= to && to <= today;
  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setOpen(false);
    onCustom(from, to);
  };

  return (
    <div className="xh-period" ref={boxRef}>
      <div className="xh-seg" role="radiogroup" aria-label={t('orgDashboard:overview.range.label')}>
        {RANGE_KEYS.map(key => (
          <button
            key={key}
            ref={key === 'custom' ? customRef : undefined}
            type="button"
            role="radio"
            aria-checked={range === key}
            aria-expanded={key === 'custom' ? open : undefined}
            aria-controls={key === 'custom' && open ? `${uid}-custom` : undefined}
            onClick={() => pick(key)}
            data-testid={`org-range-${key}`}
          >
            {label(key)}
          </button>
        ))}
      </div>
      {open && (
        <form id={`${uid}-custom`} className="xh-pop" role="dialog" aria-label={t('listPage:dateRange.customTitle')} onSubmit={apply}>
          <div className="xh-pop-row">
            <label className="xh-pop-field">
              <span>{t('listPage:dateRange.from')}</span>
              <input type="date" value={from} max={to || today} onChange={e => setFrom(e.target.value)} data-testid="org-range-from" />
            </label>
            <label className="xh-pop-field">
              <span>{t('listPage:dateRange.to')}</span>
              <input type="date" value={to} min={from} max={today} onChange={e => setTo(e.target.value)} data-testid="org-range-to" />
            </label>
          </div>
          {!valid && <p className="xh-pop-error" role="alert">{t('listPage:dateRange.invalid')}</p>}
          <div className="xh-pop-actions">
            <button type="button" className="xh-btn" onClick={() => setOpen(false)}>{t('listPage:dateRange.cancel')}</button>
            <button type="submit" className="xh-btn xh-btn-primary" disabled={!valid} data-testid="org-range-apply">
              {t('listPage:dateRange.apply')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

/* ───────── εξαγωγή ───────── */

const ExportMenu: React.FC<{ onReport: (f: 'pdf' | 'excel') => void; onExcel: () => void; exporting: boolean }> = ({ onReport, onExcel, exporting }) => {
  const { t } = useTranslation(['orgDashboard']);
  const uid = useId();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const run = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div className="xh-period" ref={boxRef}>
      <button
        ref={btnRef}
        type="button"
        className="xh-btn"
        aria-expanded={open}
        aria-controls={open ? `${uid}-menu` : undefined}
        onClick={() => setOpen(o => !o)}
        data-testid="org-export"
      >
        {exporting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Download aria-hidden="true" />}
        {t('orgDashboard:overview.export.button')}
      </button>
      {open && (
        <div id={`${uid}-menu`} className="xh-pop org-menu" data-testid="org-export-menu">
          <button type="button" className="org-menu-item" onClick={() => run(() => onReport('pdf'))}
            title={t('orgDashboard:overview.export.pdfTitle')} data-testid="org-export-pdf">
            <FileText aria-hidden="true" />
            <span>
              <b>{t('orgDashboard:overview.export.pdf')}</b>
              <span>{t('orgDashboard:overview.export.pdfHint')}</span>
            </span>
          </button>
          <button type="button" className="org-menu-item" onClick={() => run(() => onReport('excel'))}
            title={t('orgDashboard:overview.export.excelTitle')} data-testid="org-export-excel">
            <FileSpreadsheet aria-hidden="true" />
            <span>
              <b>{t('orgDashboard:overview.export.excel')}</b>
              <span>{t('orgDashboard:overview.export.excelHint')}</span>
            </span>
          </button>
          <button type="button" className="org-menu-item" onClick={() => run(onExcel)} disabled={exporting}
            title={t('orgDashboard:overview.export.productsTitle')} data-testid="org-export-products">
            {exporting ? <Loader2 className="animate-spin" aria-hidden="true" /> : <FileSpreadsheet aria-hidden="true" />}
            <span>
              <b>{t('orgDashboard:overview.export.products')}</b>
              <span>{t('orgDashboard:overview.export.productsHint')}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

/* ───────── ενότητες ───────── */

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className="xh-empty">{children}</p>;

function daysSection(t: TFn, lang: string, A: OrgAnalytics) {
  const series = A.timeseries || [];
  const n = series.length;
  const total = series.reduce((s, d) => s + (d.revenue || 0), 0);
  const avg = n > 0 ? total / n : 0;
  const best = n > 0 ? series.reduce((bi, d, i) => (d.revenue > series[bi].revenue ? i : bi), 0) : -1;
  const step = n > 10 ? Math.ceil(n / 8) : 1;
  const data: BarDatum[] = series.map((d, i) => ({
    key: d.date,
    value: d.revenue,
    // Από το τέλος (η σημερινή/τελευταία πάντα), με ίσα κενά — ώστε η τελευταία να μην πέφτει πάνω στην προηγούμενη.
    label: (n <= 10 || (n - 1 - i) % step === 0) ? (n <= 10 ? `${fmtWeekdayShort(d.date, lang)} ${Number(d.date.slice(8))}` : fmtDM(d.date)) : '',
    strong: i === best && d.revenue > 0,
    showValue: i === best && d.revenue > 0,
    tip: `${fmtWeekday(d.date, lang)} ${fmtDM(d.date)} · ${formatMoney(d.revenue)} · ${t('orgDashboard:overview.ordersCount', { count: d.orders, n: fmtNum(d.orders) })}`,
  }));
  return (
    <SheetSection
      id="org-days"
      title={t('orgDashboard:overview.days.title')}
      indexLabel={t('orgDashboard:overview.days.index')}
      hint={t('orgDashboard:overview.days.hint')}
      testId="org-section-days"
      aside={n > 0 ? (
        <>
          <Fig value={fmtEur0(avg)} sub={t('orgDashboard:overview.days.figSub')} testId="org-days-fig" />
          {best >= 0 && series[best].revenue > 0 && (
            <Note>{rich(t('orgDashboard:overview.days.best', { day: `${fmtWeekday(series[best].date, lang)} ${fmtDM(series[best].date)}`, value: formatMoney(series[best].revenue) }))}</Note>
          )}
        </>
      ) : undefined}
    >
      {n > 0 ? (
        <Bars
          data={data}
          height={200}
          format={fmtEur0}
          maxBarWidth={24}
          barRatio={0.42}
          reference={n > 1 ? { value: avg, label: t('orgDashboard:overview.days.avg', { value: fmtEur0(avg) }) } : null}
          ariaLabel={t('orgDashboard:overview.days.chart')}
          testId="org-days-chart"
        />
      ) : <Empty>{t('orgDashboard:overview.noData')}</Empty>}
    </SheetSection>
  );
}

const hh = (h: number) => String(h).padStart(2, '0');

function hoursSection(t: TFn, A: OrgAnalytics) {
  const map = new Map((A.by_hour || []).map(h => [h.hour, h]));
  const all = Array.from({ length: 24 }, (_, i) => map.get(i) || { hour: i, orders: 0, revenue: 0 });
  // Μόνο οι ώρες με κίνηση (από την πρώτη ως την τελευταία): 24 στήλες με τις μισές άδειες δεν διαβάζονται.
  const first = all.findIndex(h => h.orders > 0);
  const last = all.length - 1 - [...all].reverse().findIndex(h => h.orders > 0);
  const rows = first >= 0 ? all.slice(first, last + 1) : [];
  const peak = rows.length > 0 ? rows.reduce((b, h) => (h.orders > b.orders ? h : b), rows[0]) : null;
  const data: BarDatum[] = rows.map(h => ({
    key: String(h.hour),
    value: h.orders,
    label: h.hour % 2 === 0 ? hh(h.hour) : '',
    strong: !!peak && h.hour === peak.hour,
    showValue: !!peak && h.hour === peak.hour,
    tip: `${hh(h.hour)}:00–${hh((h.hour + 1) % 24)}:00 · ${t('orgDashboard:overview.ordersCount', { count: h.orders, n: fmtNum(h.orders) })} · ${formatMoney(h.revenue)}`,
  }));
  return (
    <SheetSection
      id="org-hours"
      title={t('orgDashboard:overview.hours.title')}
      hint={t('orgDashboard:overview.hours.hint')}
      testId="org-section-hours"
      aside={peak ? (
        <Fig
          value={`${hh(peak.hour)}:00–${hh((peak.hour + 1) % 24)}:00`}
          sub={t('orgDashboard:overview.hours.figSub', { orders: t('orgDashboard:overview.ordersCount', { count: peak.orders, n: fmtNum(peak.orders) }) })}
          testId="org-hours-fig"
        />
      ) : undefined}
    >
      {rows.length > 0
        ? <Bars data={data} height={178} format={fmtNum} maxBarWidth={20} barRatio={0.56} ariaLabel={t('orgDashboard:overview.hours.chart')} testId="org-hours-chart" />
        : <Empty>{t('orgDashboard:overview.noData')}</Empty>}
    </SheetSection>
  );
}

function dowSection(t: TFn, A: OrgAnalytics) {
  const map = new Map((A.by_dow || []).map(d => [d.dow, d]));
  const rows = WEEK.map(dow => map.get(dow) || { dow, orders: 0, revenue: 0 });
  const any = rows.some(d => d.revenue > 0 || d.orders > 0);
  const best = rows.reduce((b, d) => (d.revenue > b.revenue ? d : b), rows[0]);
  const data: BarDatum[] = rows.map(d => ({
    key: String(d.dow),
    value: d.revenue,
    label: t(`orgDashboard:overview.dow.short.${d.dow}`),
    strong: any && d.dow === best.dow,
    showValue: any && d.dow === best.dow,
    tip: `${t(`orgDashboard:overview.dow.long.${d.dow}`)} · ${t('orgDashboard:overview.ordersCount', { count: d.orders, n: fmtNum(d.orders) })} · ${formatMoney(d.revenue)}`,
  }));
  return (
    <SheetSection
      id="org-dow"
      title={t('orgDashboard:overview.dow.title')}
      indexLabel={t('orgDashboard:overview.dow.index')}
      hint={t('orgDashboard:overview.dow.hint')}
      testId="org-section-dow"
      aside={any ? <Fig value={t(`orgDashboard:overview.dow.long.${best.dow}`)} sub={t('orgDashboard:overview.dow.figSub', { value: formatMoney(best.revenue) })} /> : undefined}
    >
      {any
        ? <Bars data={data} height={170} format={fmtEur0} maxBarWidth={36} barRatio={0.5} ariaLabel={t('orgDashboard:overview.dow.chart')} testId="org-dow-chart" />
        : <Empty>{t('orgDashboard:overview.noData')}</Empty>}
    </SheetSection>
  );
}

function channelsSection(t: TFn, A: OrgAnalytics) {
  const rows = [...(A.by_channel || [])].sort((a, b) => b.revenue - a.revenue);
  const total = rows.reduce((s, c) => s + Math.max(0, c.revenue), 0);
  const share = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  const top = rows[0];
  return (
    <SheetSection
      id="org-channels"
      title={t('orgDashboard:overview.channels.title')}
      hint={t('orgDashboard:overview.channels.hint')}
      testId="org-section-channels"
      aside={top && total > 0 ? <Fig value={fmtPct(share(top.revenue))} sub={t('orgDashboard:overview.channels.figSub', { name: orderTypeLabel(t, top.channel) })} /> : undefined}
    >
      {rows.length > 0 ? (
        <>
          {total > 0 && (
            <StackedBar
              parts={rows.map((c, i) => ({ key: c.channel, label: orderTypeLabel(t, c.channel), value: Math.max(0, c.revenue), tone: toneOf(i), tip: `${orderTypeLabel(t, c.channel)} · ${formatMoney(c.revenue)} · ${fmtPct(share(c.revenue))}` }))}
              testId="org-channels-stack"
            />
          )}
          <table className="xh-tbl" data-testid="org-channels-table">
            <thead>
              <tr>
                <th>{t('orgDashboard:overview.channels.colChannel')}</th>
                <th>{t('orgDashboard:overview.colOrders')}</th>
                <th>{t('orgDashboard:overview.colRevenue')}</th>
                <th>{t('orgDashboard:overview.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c.channel}>
                  <td><span className="xh-nm"><span className={`xh-sw bg${toneOf(i)}`} aria-hidden="true" /><span className="xh-trunc">{orderTypeLabel(t, c.channel)}</span></span></td>
                  <td>{fmtNum(c.orders)}</td>
                  <td className="strong">{formatMoney(c.revenue)}</td>
                  <td>{fmtPct(share(c.revenue))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : <Empty>{t('orgDashboard:overview.channels.empty')}</Empty>}
    </SheetSection>
  );
}

function paymentsSection(t: TFn, A: OrgAnalytics) {
  const rows = [...(A.by_payment || [])].sort((a, b) => b.amount - a.amount);
  const total = rows.reduce((s, p) => s + Math.max(0, p.amount), 0);
  const share = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  const top = rows[0];
  return (
    <SheetSection
      id="org-payments"
      title={t('orgDashboard:overview.payments.title')}
      hint={t('orgDashboard:overview.payments.hint')}
      testId="org-section-payments"
      aside={top && total > 0 ? <Fig value={fmtPct(share(top.amount))} sub={t('orgDashboard:overview.payments.figSub', { name: paymentLabel(t, top.method) })} /> : undefined}
    >
      {rows.length > 0 ? (
        <>
          {total > 0 && (
            <StackedBar
              parts={rows.map((p, i) => ({ key: p.method, label: paymentLabel(t, p.method), value: Math.max(0, p.amount), tone: toneOf(i), tip: `${paymentLabel(t, p.method)} · ${formatMoney(p.amount)} · ${fmtPct(share(p.amount))}` }))}
              testId="org-payments-stack"
            />
          )}
          <table className="xh-tbl" data-testid="org-payments-table">
            <thead>
              <tr>
                <th>{t('orgDashboard:overview.payments.colMethod')}</th>
                <th>{t('orgDashboard:overview.payments.colCount')}</th>
                <th>{t('orgDashboard:overview.payments.colAmount')}</th>
                <th>{t('orgDashboard:overview.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={p.method}>
                  <td><span className="xh-nm"><span className={`xh-sw bg${toneOf(i)}`} aria-hidden="true" /><span className="xh-trunc">{paymentLabel(t, p.method)}</span></span></td>
                  <td>{fmtNum(p.count)}</td>
                  <td className="strong">{formatMoney(p.amount)}</td>
                  <td>{fmtPct(share(p.amount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : <Empty>{t('orgDashboard:overview.noData')}</Empty>}
    </SheetSection>
  );
}

function rankSection(t: TFn, id: 'products' | 'categories', rows: Array<{ name: string; qty: number; revenue: number }>) {
  const top = rows[0];
  return (
    <SheetSection
      id={`org-${id}`}
      title={t(`orgDashboard:overview.${id}.title`)}
      indexLabel={t(`orgDashboard:overview.${id}.index`)}
      hint={t(`orgDashboard:overview.${id}.hint`, { count: rows.length })}
      testId={`org-section-${id}`}
      aside={top ? <Fig value={top.name} sub={t('orgDashboard:overview.rankFigSub', { units: fmtNum(top.qty), amount: formatMoney(top.revenue) })} /> : undefined}
    >
      {rows.length > 0 ? (
        <table className="xh-tbl" data-testid={`org-${id}-table`}>
          <thead>
            <tr>
              <th>{t(`orgDashboard:overview.${id}.col`)}</th>
              <th>{t('orgDashboard:overview.colUnits')}</th>
              <th>{t('orgDashboard:overview.colRevenue')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={`${p.name}-${i}`}>
                <td><span className="xh-nm"><span className="rk">{i + 1}</span><span className="xh-trunc" title={p.name}>{p.name}</span></span></td>
                <td>{fmtNum(p.qty)}</td>
                <td className="strong">{formatMoney(p.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <Empty>{t('orgDashboard:overview.noData')}</Empty>}
    </SheetSection>
  );
}

function financeSection(t: TFn, A: OrgAnalytics) {
  const fin = A.financial || {};
  return (
    <SheetSection
      id="org-finance"
      title={t('orgDashboard:overview.finance.title')}
      hint={fin.vat_from_documents ? t('orgDashboard:overview.finance.hintDocs') : t('orgDashboard:overview.finance.hintEstimate')}
      testId="org-section-finance"
      aside={<Fig value={formatMoney(fin.gross || 0)} sub={t('orgDashboard:overview.finance.gross')} />}
    >
      <KeyValue
        testId="org-finance-values"
        items={[
          { id: 'gross', label: t('orgDashboard:overview.finance.gross'), value: formatMoney(fin.gross || 0) },
          { id: 'net', label: t('orgDashboard:overview.finance.net'), value: formatMoney(fin.net || 0) },
          {
            id: 'vat',
            label: fin.vat_from_documents ? t('orgDashboard:overview.finance.vatDocs') : t('orgDashboard:overview.finance.vatEstimate'),
            value: formatMoney(fin.vat || 0),
          },
          { id: 'tips', label: t('orgDashboard:overview.finance.tips'), value: formatMoney(fin.tips || 0) },
          { id: 'fees', label: t('orgDashboard:overview.finance.fees'), value: formatMoney((fin.env_fees || 0) + (fin.delivery_fees || 0)) },
          { id: 'cancelled', label: t('orgDashboard:overview.finance.cancelled'), value: formatMoney(fin.cancelled_value || 0) },
        ]}
      />
    </SheetSection>
  );
}

function storesSection(t: TFn, A: OrgAnalytics, switching: number | null, onSwitchStore: (id: number) => void) {
  const rows = A.by_store || [];
  const top = rows.length > 0 ? rows.reduce((b, s) => (s.revenue > b.revenue ? s : b), rows[0]) : null;
  return (
    <SheetSection
      id="org-stores"
      title={t('orgDashboard:overview.stores.title')}
      indexLabel={t('orgDashboard:overview.stores.index')}
      hint={t('orgDashboard:overview.stores.hint')}
      testId="org-section-stores"
      aside={top && top.revenue > 0 ? <Fig value={storeLabel(top)} sub={t('orgDashboard:overview.stores.figSub', { amount: formatMoney(top.revenue) })} /> : undefined}
    >
      {rows.length > 0 ? (
        <table className="xh-tbl org-stores-tbl" data-testid="org-stores-compare">
          <thead>
            <tr>
              <th>{t('orgDashboard:overview.stores.colStore')}</th>
              <th>{t('orgDashboard:overview.colOrders')}</th>
              <th className="xh-opt">{t('orgDashboard:overview.stores.colAov')}</th>
              <th>{t('orgDashboard:overview.colRevenue')}</th>
              <th className="xh-opt">{t('orgDashboard:overview.stores.colChange')}</th>
              <th><span className="sr-only">{t('orgDashboard:stores.enter')}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(s => {
              const v = pctChange(s.revenue, s.revenue_prev);
              return (
                <tr key={s.id} className={s.is_active ? undefined : 'muted'}>
                  <td>
                    <span className="xh-trunc" title={storeLabel(s)}>{storeLabel(s)}</span>
                    {(s.display_name || !s.is_active) && (
                      <span className="xh-trunc sub org-sub">
                        {[s.display_name ? s.name : null, s.is_active ? null : t('common:inactive')].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </td>
                  <td>{fmtNum(s.orders)}</td>
                  <td className="xh-opt">{formatMoney(s.aov)}</td>
                  <td className="strong">{formatMoney(s.revenue)}</td>
                  <td className={`xh-opt ${v > 0 ? 'ok' : v < 0 ? 'bad' : 't3'}`}>{v === 0 ? '—' : `${arrow(v)} ${fmtNum(Math.abs(v))}%`}</td>
                  <td>
                    <button
                      type="button"
                      className="org-enter"
                      onClick={() => onSwitchStore(s.id)}
                      disabled={switching === s.id}
                      title={t('admin:enterStoreTitle')}
                      data-testid={`org-enter-${s.id}`}
                    >
                      {switching === s.id ? <Loader2 className="animate-spin" aria-hidden="true" /> : <ArrowRightLeft aria-hidden="true" />}
                      {t('admin:enterStore')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <Empty>{t('admin:noStoresInOrg')}</Empty>}
    </SheetSection>
  );
}

function wasteSection(t: TFn, A: OrgAnalytics) {
  const rows = A.waste_by_store || [];
  if (rows.length === 0) return null;
  // Το «% τζίρου» κάνει τη σύγκριση δίκαιη ανεξαρτήτως μεγέθους, και η «τελευταία καταχώρηση» ξεσκεπάζει
  // όσους δεν τη γράφουν καθόλου (αλλιώς το 0 διαβάζεται σαν μηδενική φύρα).
  const revById = new Map((A.by_store || []).map(s => [s.id, s.revenue]));
  const totQty = rows.reduce((a, w) => a + w.qty, 0);
  const totVal = rows.reduce((a, w) => a + w.value, 0);
  const pct2 = new Intl.NumberFormat('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <SheetSection
      id="org-waste"
      title={t('orgDashboard:overview.waste.title')}
      indexLabel={t('orgDashboard:overview.waste.index')}
      hint={t('orgDashboard:overview.waste.hint')}
      testId="org-section-waste"
      aside={<Fig value={formatMoney(totVal)} sub={t('orgDashboard:overview.waste.figSub', { qty: fmtNum(totQty) })} />}
    >
      <table className="xh-tbl" data-testid="org-waste-table">
        <thead>
          <tr>
            <th>{t('orgDashboard:overview.stores.colStore')}</th>
            <th className="xh-opt">{t('orgDashboard:overview.waste.colLast')}</th>
            <th>{t('orgDashboard:overview.waste.colShare')}</th>
            <th className="xh-opt">{t('orgDashboard:overview.colUnits')}</th>
            <th>{t('orgDashboard:overview.waste.colValue')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(w => {
            const rev = revById.get(w.id) || 0;
            const pctRev = rev > 0 ? (w.value / rev) * 100 : 0;
            // Ημερολογιακές μέρες Ελλάδας («σήμερα» = ίδια ημερομηνία), όχι 24ωρα: στις 00:30 μια φύρα των 23:00 είναι «χθες».
            const lastYmd = w.last_entry == null ? null : toAthensYmd(w.last_entry);
            const never = lastYmd == null;
            const dAgo = never ? null : Math.max(0, daysBetween(lastYmd, athensYmd()));
            const stale = !never && (dAgo as number) > 3;
            return (
              <tr key={w.id}>
                <td><span className="xh-trunc" title={w.store}>{w.store}</span></td>
                <td className={`xh-opt ${never ? 'bad' : stale ? 'org-warn' : ''}`}>
                  {never ? t('orgDashboard:overview.waste.never') : dAgo === 0 ? t('orgDashboard:overview.waste.today') : t('orgDashboard:overview.waste.daysAgo', { count: dAgo as number })}
                </td>
                <td className={pctRev > 2 ? 'bad' : pctRev > 1 ? 'org-warn' : ''}>{rev > 0 ? `${pct2.format(pctRev)}%` : '—'}</td>
                <td className="xh-opt">{fmtNum(w.qty)}</td>
                <td className="strong">{formatMoney(w.value)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SheetSection>
  );
}

function catalogSection(t: TFn, A: OrgAnalytics) {
  const h = A.catalog_health || {};
  const warn = (n: number | undefined) => ((n || 0) > 0 ? <span className="org-warn">{fmtNum(n || 0)}</span> : fmtNum(n || 0));
  return (
    <SheetSection
      id="org-catalog"
      title={t('orgDashboard:overview.catalog.title')}
      hint={t('orgDashboard:overview.catalog.hint')}
      testId="org-section-catalog"
    >
      <KeyValue
        testId="org-catalog-values"
        items={[
          { id: 'products', label: t('orgDashboard:overview.catalog.products'), value: fmtNum(h.products || 0) },
          { id: 'priceLists', label: t('orgDashboard:overview.catalog.priceLists'), value: fmtNum(h.price_lists || 0) },
          { id: 'optionTemplates', label: t('orgDashboard:overview.catalog.optionTemplates'), value: fmtNum(h.option_templates || 0) },
          { id: 'sizeTemplates', label: t('orgDashboard:overview.catalog.sizeTemplates'), value: fmtNum(h.size_templates || 0) },
          { id: 'untemplatized', label: t('orgDashboard:overview.catalog.untemplatized'), value: warn(h.untemplatized_groups) },
          { id: 'sizesNoPrice', label: t('orgDashboard:overview.catalog.sizesNoPrice'), value: warn(h.sizes_no_price) },
        ]}
      />
      {(h.pending_approvals || 0) > 0 && (
        <div className="mt-4">
          <Notice testId="org-pending-approvals">{t('orgDashboard:overview.catalog.pending', { count: h.pending_approvals })}</Notice>
        </div>
      )}
    </SheetSection>
  );
}

function buildKpis(t: TFn, A: OrgAnalytics): KpiItem[] {
  const k = A.kpis || {};
  const series = (A.timeseries || []).map(d => d.revenue);
  const cancelled = k.cancelled_orders || 0;
  const rate = k.cancel_rate || 0;
  // Ο server συγκρίνει με το ίσο διάστημα ακριβώς πριν (ίδιος αριθμός ημερών).
  const r = A.range;
  const suffix = r && r.days === 1 ? t('orgDashboard:overview.kpi.vsYesterday') : t('orgDashboard:overview.kpi.vsPrevious');
  const title = r && isYmd(r.from)
    ? t('orgDashboard:overview.kpi.vsTitle', { from: fmtDMY(addDaysYmd(r.from, -(r.days || 1))), to: fmtDMY(addDaysYmd(r.from, -1)) })
    : undefined;
  const deltaOf2 = (cur: number, prev: number) => deltaOf(cur, prev, suffix, title);
  return [
    {
      id: 'revenue', label: t('orgDashboard:overview.kpi.revenue'), value: fmtMoney(k.revenue || 0), unit: '€', hero: true,
      delta: deltaOf2(k.revenue || 0, k.revenue_prev || 0),
      spark: series.length > 1 ? series : undefined, sparkLabel: t('orgDashboard:overview.kpi.spark'),
    },
    {
      id: 'orders', label: t('orgDashboard:overview.kpi.orders'), value: fmtNum(k.orders || 0),
      delta: deltaOf2(k.orders || 0, k.orders_prev || 0),
      rows: [t('orgDashboard:overview.kpi.items', { n: fmtNum(k.items || 0), count: k.items || 0 })],
    },
    {
      id: 'aov', label: t('orgDashboard:overview.kpi.aov'), value: fmtMoney(k.aov || 0), unit: '€',
      delta: deltaOf2(k.aov || 0, k.aov_prev || 0),
    },
    {
      id: 'customers', label: t('orgDashboard:overview.kpi.customers'), value: fmtNum(k.customers || 0),
      delta: deltaOf2(k.customers || 0, k.customers_prev || 0),
    },
    {
      id: 'cancelled', label: t('orgDashboard:overview.kpi.cancelled'), value: fmtNum(cancelled),
      rows: [t('orgDashboard:overview.kpi.cancelRate', { pct: fmtPct(rate) })],
    },
  ];
}

/* ───────── σελίδα ───────── */

const OrgOverviewTab: React.FC<OrgOverviewTabProps> = ({ analytics, stores, switching, onSwitchStore, onOpenReport, onExportExcel, exporting }) => {
  const { t: tRaw, i18n } = useTranslation(['orgDashboard', 'admin', 'common', 'listPage']);
  const t = tRaw as unknown as TFn;
  const lang = i18n.language;
  const rootRef = useRef<HTMLDivElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const { data: A, loading, error, filterStoreIds, setFilterStoreIds } = analytics;
  const vizStyle = useVizTones(rootRef, [!!A]);

  const range = A?.range;
  const periodText = range
    ? t('orgDashboard:overview.period', { from: fmtDMY(range.from), to: fmtDMY(range.to), count: range.days, n: fmtNum(range.days) })
    : t('common:loading');
  const storesText = filterStoreIds.length === 0
    ? t('orgDashboard:overview.filter.allStores', { count: stores.length })
    : t('orgDashboard:overview.filter.selected', { count: filterStoreIds.length });
  const subtitle = `${periodText} · ${storesText}`;

  const kpis = useMemo(() => (A ? buildKpis(t, A) : []), [A, t]);

  const tools = (
    <>
      <OrgPeriodPicker
        range={analytics.range}
        customFrom={analytics.customFrom}
        customTo={analytics.customTo}
        onRange={analytics.setRange}
        onCustom={analytics.setCustomRange}
      />
      <button
        type="button"
        className="xh-btn"
        aria-expanded={filterOpen}
        aria-controls={filterOpen ? 'org-store-filter' : undefined}
        onClick={() => setFilterOpen(o => !o)}
        data-testid="org-filter-toggle"
      >
        <StoreIcon aria-hidden="true" />
        {filterStoreIds.length === 0
          ? t('orgDashboard:overview.filter.buttonAll', { count: stores.length })
          : t('orgDashboard:overview.filter.buttonSome', { count: filterStoreIds.length })}
      </button>
      <ExportMenu onReport={onOpenReport} onExcel={onExportExcel} exporting={exporting} />
    </>
  );

  const toggleStore = (id: number) => setFilterStoreIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  let body: React.ReactNode;
  if (!A && !error) {
    body = (
      <>
        <KpisSkeleton />
        <SectionsSkeleton />
      </>
    );
  } else if (!A && error) {
    body = (
      <SheetBlock>
        <LoadFailed
          message={error === 'forbidden' ? t('orgDashboard:overview.forbidden') : t('orgDashboard:overview.loadFailed')}
          onRetry={analytics.reload}
        />
      </SheetBlock>
    );
  } else if (A) {
    body = (
      <div className={`xh-body${loading ? ' is-stale' : ''}`} aria-busy={loading || undefined} data-testid="org-overview-body">
        <KpiStrip items={kpis} ariaLabel={t('orgDashboard:overview.kpi.title')} />
        {daysSection(t, lang, A)}
        {hoursSection(t, A)}
        {dowSection(t, A)}
        {channelsSection(t, A)}
        {paymentsSection(t, A)}
        {rankSection(t, 'products', A.top_products || [])}
        {rankSection(t, 'categories', (A.top_categories || []).map(c => ({ name: c.category, qty: c.qty, revenue: c.revenue })))}
        {financeSection(t, A)}
        {storesSection(t, A, switching, onSwitchStore)}
        {wasteSection(t, A)}
        {catalogSection(t, A)}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="xh-page org-overview" style={vizStyle} data-testid="org-overview">
      {/* Κενός τίτλος: την κεφαλίδα (h1 + χειριστήρια) τη γράφει το HomeHeader· του φύλλου κρύβεται (home.css). */}
      <SettingsSheet title="">
        <HomeHeader
          crumb={t('orgDashboard:nav.groups.overview')}
          title={t('orgDashboard:overview.title')}
          subtitle={subtitle}
          tools={tools}
        />
        {filterOpen && (
          <div id="org-store-filter" className="org-filter" data-testid="org-store-filter">
            <div className="org-filter-head">
              <span className="org-filter-title">{t('orgDashboard:overview.filter.title')}</span>
              <span className="org-filter-actions">
                <button type="button" className="xh-link" aria-pressed={filterStoreIds.length === 0} onClick={() => setFilterStoreIds([])}>
                  {t('orgDashboard:overview.filter.all')}
                </button>
                <button type="button" className="xh-link" onClick={() => setFilterStoreIds(stores.map(s => s.id))}>
                  {t('orgDashboard:overview.filter.selectAll')}
                </button>
                {filterStoreIds.length > 0 && (
                  <button type="button" className="xh-link xh-link-muted" onClick={() => setFilterStoreIds([])}>
                    {t('common:clear')}
                  </button>
                )}
              </span>
            </div>
            <p className="org-filter-hint">{t('orgDashboard:overview.filter.hint')}</p>
            <div className="org-filter-grid">
              {stores.map(s => {
                const checked = filterStoreIds.includes(s.id);
                return (
                  <label key={s.id} className="org-check" data-checked={checked || undefined}>
                    <input type="checkbox" checked={checked} onChange={() => toggleStore(s.id)} />
                    <span className="truncate">{storeLabel(s)} <span className="text-t4">#{s.id}</span></span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {body}
      </SettingsSheet>
    </div>
  );
};

export default OrgOverviewTab;
